import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getRangeFromDateAndView, TimezoneSelector } from "@tellescope/calendar";
import { usePermissionQuery, useUsersQuery } from "@tellescope/tanstack-query";
import {
  Checkbox,
  Combobox,
  type ComboboxOption,
  DeletableBadge,
  Label,
  PageTabs,
  PageTabsList,
  PageTabsTrigger,
  Switch,
} from "@tellescope/ui";
import { formatName } from "@tellescope/utils";
import { PageTabsHeader } from "../../components";
import { useShallowCalendarStore } from "../../store";
import { Link, useLocation } from "react-router-dom";
import { makeCalendarRouteFromRangeAndView } from "./utils";

export const CalendarPageTabsHeader = () => {
  const { t } = useTranslation("calendar");
  const {
    lastSelectedViewType,
    lastSelectedViewId,
    selectedTimezone,
    setSelectedTimezone,
    selectedUserIds,
    setSelectedUserIds,
    showAvailabilityOverlay,
    setShowAvailabilityOverlay,
  } = useShallowCalendarStore(
    "lastSelectedViewType",
    "lastSelectedViewId",
    "selectedTimezone",
    "setSelectedTimezone",
    "selectedUserIds",
    "setSelectedUserIds",
    "showAvailabilityOverlay",
    "setShowAvailabilityOverlay"
  );
  const location = useLocation();
  const selectedTab = useMemo(() => {
    const subtab = location.pathname.split("/")[2];
    if (subtab === "availability" || subtab === "locations" || subtab === "templates" || subtab === "booking-pages") {
      return subtab;
    }
    return "calendar";
  }, [location.pathname]);

  const { data: users = [] } = useUsersQuery();
  const userOptions = useMemo<ComboboxOption[]>(
    () =>
      users.map((user) => ({
        value: user.id,
        label: formatName(user) || user.id,
      })),
    [users]
  );
  const selectedUserOptions = useMemo<ComboboxOption[]>(
    () => selectedUserIds.map((id) => userOptions.find((o) => o.value === id) ?? { value: id, label: id }),
    [selectedUserIds, userOptions]
  );

  const {
    read: { access: canReadLocations },
  } = usePermissionQuery({
    model: "appointment_locations",
    actions: ["read"],
  });
  const {
    read: { access: canReadTemplates },
  } = usePermissionQuery({
    model: "calendar_event_templates",
    actions: ["read"],
  });
  const {
    read: { access: canReadBookingPages },
  } = usePermissionQuery({
    model: "appointment_booking_pages",
    actions: ["read"],
  });

  return (
    <PageTabsHeader className="pr-3">
      <div className="flex h-full w-full justify-between items-center">
        <PageTabs value={selectedTab} className="h-full">
          <PageTabsList>
            <PageTabsTrigger value="calendar" asChild>
              <Link
                to={makeCalendarRouteFromRangeAndView(lastSelectedViewId)({
                  newViewType: lastSelectedViewType,
                  ...getRangeFromDateAndView(new Date(), lastSelectedViewType),
                })}
              >
                {t("Calendar")}
              </Link>
            </PageTabsTrigger>
            <PageTabsTrigger value="availability" asChild>
              <Link to="/calendar/availability">{t("Availability")}</Link>
            </PageTabsTrigger>
            {canReadLocations && (
              <PageTabsTrigger value="locations" asChild>
                <Link to="/calendar/locations">{t("Locations")}</Link>
              </PageTabsTrigger>
            )}
            {canReadTemplates && (
              <PageTabsTrigger value="templates" asChild>
                <Link to="/calendar/templates">{t("Templates")}</Link>
              </PageTabsTrigger>
            )}
            {canReadBookingPages && (
              <PageTabsTrigger value="booking-pages" asChild>
                <Link to="/calendar/booking-pages">{t("Booking Pages")}</Link>
              </PageTabsTrigger>
            )}
          </PageTabsList>
        </PageTabs>
        <div className="flex items-center gap-2">
          {selectedUserOptions.length === 1 && (
            <span className="flex items-center gap-1">
              <Switch
                checked={showAvailabilityOverlay}
                onCheckedChange={(val) => setShowAvailabilityOverlay(val)}
                className="data-[state=checked]:bg-green-600"
                id="show-availability-overlay"
              />
              <Label htmlFor="show-availability-overlay" className="font-normal text-sm">
                {t("Availability")}
              </Label>
            </span>
          )}
          <Combobox
            data-testid="calendar-user-quick-select"
            value={selectedUserOptions}
            onChange={(selected) => {
              const ids = Array.isArray(selected) ? selected.map((s) => s.value) : [];
              setSelectedUserIds(ids);
            }}
            options={userOptions}
            multiple
            placeholder={t("All users")}
            searchPlaceholder={t("Search users...")}
            formatSelectedValue={(selected) => {
              const selectedArray = Array.isArray(selected) ? selected : [selected];
              if (selectedArray.length === 1) {
                const only = selectedArray[0];
                return (
                  <DeletableBadge
                    onDelete={() => setSelectedUserIds([])}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {only?.label}
                  </DeletableBadge>
                );
              }
              return t("{{amount}} selected", { amount: selectedArray.length });
            }}
            formatOption={(option) => (
              <div
                key={option.option.value}
                data-testid={`calendar-user-quick-select-item-${option.option.value}`}
                className="flex items-center gap-2 w-full cursor-pointer"
                role="button"
                onClickCapture={(e) => {
                  const isSelected = selectedUserIds.includes(option.option.value);
                  e.stopPropagation();
                  e.preventDefault();
                  setSelectedUserIds(
                    isSelected
                      ? selectedUserIds.filter((id) => id !== option.option.value)
                      : [...selectedUserIds, option.option.value]
                  );
                }}
              >
                <Checkbox checked={option.isSelected} id={option.option.value} className="flex-shrink-0" />
                <Label
                  htmlFor={option.option.value}
                  className="font-light pointer-events-none block line-clamp-2 min-w-0"
                >
                  {option.option.label}
                </Label>
              </div>
            )}
            popoverAlign="end"
            popoverClassName="w-[350px]"
          />
          <TimezoneSelector value={selectedTimezone} onChange={setSelectedTimezone} />
        </div>
      </div>
    </PageTabsHeader>
  );
};
